import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Breakpoints, Colors, Index } from '@client/style/Variables';
import { TextStyle } from '@client/style/TextStyles';
import { gsap } from 'gsap/all';
import { commonTextStyle } from '@client/style/Text';
import Functions from '@client/style/Functions';
import CloseIcon from '@client/assets/svgs/closeIcon.svg';
import Link from '@core/PageSwitch/Link';
import { ILinkData } from '@core/PageSwitch/ILinkData';
import { SquareButton } from '../shared/SquareButton';
import { PapandAnimation } from './PapandAnimation';

const StyledContainer = styled.button`
	display: block;
	appearance: none;
	position: fixed;
	background: transparent;
	border: none;
	bottom: 20px;
	right: 0;
	width: 150px;
	height: 150px;
	z-index: ${Index.papand};
	cursor: pointer;

	${Functions.breakpoint(Breakpoints.laptop)} {
		right: 30px;
		bottom: 40px;
		transform: scale(1.5);
	}
`;

const StyledOverlay = styled.div`
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	background: rgba(0, 0, 0, 0.7);
	z-index: ${Index.papand};
	display: flex;
	align-items: end;
	justify-content: center;
	padding-bottom: 150px;
`;

const StyledPopup = styled.div`
	position: relative;
	background: #f8f1eb;
	padding: 20px;
	margin-right: 15px;
	margin-left: 25px;
	display: flex;
	flex-direction: column;
	max-height: 90vh;
	align-items: center;

	${Functions.breakpoint(Breakpoints.laptop)} {
		padding: 80px;
		max-width: 1220px;
		max-height: 80vh;
	}
`;

const StyledCloseButton = styled.button`
	position: absolute;
	appearance: none;
	margin: 0;
	padding: 0;
	top: 20px;
	right: 20px;
	width: 30px;
	height: 30px;
	display: flex;
	align-items: center;
	justify-content: center;

	svg {
		display: block;
		color: ${Colors.darkText};
	}
`;

const StyledButtonContainer = styled.div`
	margin-top: 2em;
	display: none;

	${Functions.breakpoint(Breakpoints.laptop)} {
		display: block;
	}
`;

const StyledPopupMessage = styled.div`
	${commonTextStyle}
	text-align: left;
	width: 100%;
	overflow-y: auto;

	${Functions.breakpoint(Breakpoints.laptop)} {
		text-align: center;
	}

	a {
		font-weight: bold;
		text-decoration: underline;
	}
`;

const StyledPopupTitle = styled.h2`
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	${TextStyle.size('p3')}
	margin: 0px;
	text-align: left;
	width: 100%;
	margin-bottom: 0.5em;
	padding-right: 40px;

	${Functions.breakpoint(Breakpoints.laptop)} {
		${TextStyle.size('h6')}
		text-align: center;
	}
`;

export function Papand({
	message,
}: {
	message: { title: string; text: string; link: ILinkData[]; desktopButtonText: string };
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [isHidden, setIsHidden] = useState(false);
	const containerRef = useRef<HTMLButtonElement>(null);

	if (isHidden) {
		return null;
	}

	const closeAndHide = () => {
		setIsOpen(false);

		gsap.to(containerRef.current, {
			x: '100%',
			y: '10%',
			duration: 0.8,
			ease: 'elastic.in(1.1, 0.8)',
			onComplete: () => {
				setIsHidden(true);
			},
		});
	};

	return (
		<>
			{isOpen && (
				<StyledOverlay>
					<StyledPopup>
						<StyledPopupTitle>{message.title}</StyledPopupTitle>
						<StyledCloseButton onClick={closeAndHide}>
							<CloseIcon />
						</StyledCloseButton>

						<StyledPopupMessage>
							{message.text + ' '}
							{message.link[0] && (
								<Link data={message.link[0]} onClick={() => closeAndHide()}>
									{message.link[0].title}
								</Link>
							)}
						</StyledPopupMessage>
						<StyledButtonContainer>
							<SquareButton onClick={closeAndHide} colors={Colors.darkBackground}>
								{message.desktopButtonText}
							</SquareButton>
						</StyledButtonContainer>
					</StyledPopup>
				</StyledOverlay>
			)}
			<StyledContainer onClick={() => setIsOpen(true)} ref={containerRef}>
				<PapandAnimation idleOnly={isOpen} />
			</StyledContainer>
		</>
	);
}
